/**
 * TechnicianMoreMenu Component
 *
 * Expandable menu for additional options in the Technician Portal.
 * Slides up from the bottom navigation when "More" is tapped.
 * Contains secondary navigation items and logout.
 *
 * @module components/technician/layout/TechnicianMoreMenu
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiClock,
  FiAlertTriangle,
  FiMessageCircle,
  FiUser,
  FiHelpCircle,
  FiLogOut,
  FiX,
  FiTool,
  FiCpu
} from 'react-icons/fi';
import PropTypes from 'prop-types';
import './TechnicianLayout.css';

const menuItems = [
  {
    // Tu agente: Kane OneDeck / ChatGPT + handbook. No existia en el portal
    // tecnico, asi que los tecnicos no podian llegar al agente en movil.
    path: '/agente',
    icon: FiCpu,
    label: 'Tu agente',
    description: 'Abrir Kane y ver que sabe hacer'
  },
  {
    path: '/mis-herramientas',
    icon: FiTool,
    label: 'Mis herramientas',
    description: 'Ver y devolver las herramientas que tenés'
  },
  {
    path: '/bodega',
    icon: FiTool,
    label: 'Bodega',
    description: 'Tomar una herramienta disponible'
  },
  {
    path: '/tecnico/horas',
    icon: FiClock,
    label: 'Registrar Horas',
    description: 'Control de tiempo trabajado'
  },
  {
    path: '/tecnico/incidentes',
    icon: FiAlertTriangle,
    label: 'Reportar Incidente',
    description: 'Informar problemas o situaciones'
  },
  {
    path: '/tecnico/mensajes',
    icon: FiMessageCircle,
    label: 'Mensajes',
    description: 'Comunicacion con el equipo',
    hasBadge: true
  },
  {
    path: '/tecnico/perfil',
    icon: FiUser,
    label: 'Mi Perfil',
    description: 'Configuracion y datos personales'
  },
  {
    path: '/ayuda',
    icon: FiHelpCircle,
    label: 'Ayuda',
    description: 'Manual de uso de la plataforma'
  }
];

const TechnicianMoreMenu = ({ onClose, onNavigate, onLogout, unreadMessages }) => {
  const location = useLocation();

  const handleItemClick = (path) => {
    onNavigate(path);
  };

  const handleLogoutClick = () => {
    onLogout();
  };

  return (
    <div className="more-menu">
      {/* Menu Header */}
      <div className="more-menu-header">
        <h3 className="more-menu-title">Opciones</h3>
        <button
          className="more-menu-close"
          onClick={onClose}
          aria-label="Cerrar menu"
        >
          <FiX size={24} />
        </button>
      </div>

      {/* Menu Items */}
      <div className="more-menu-items">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path);
          const showBadge = item.hasBadge && unreadMessages > 0;

          return (
            <button
              key={item.path}
              className={`more-menu-item ${isActive ? 'active' : ''}`}
              onClick={() => handleItemClick(item.path)}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="more-menu-icon">
                <Icon size={24} />
                {showBadge && (
                  <span className="more-menu-badge">
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </span>
              <div className="more-menu-text">
                <span className="more-menu-label">{item.label}</span>
                <span className="more-menu-description">{item.description}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Logout Section */}
      <div className="more-menu-footer">
        <button className="more-menu-logout" onClick={handleLogoutClick}>
          <FiLogOut size={22} />
          <span>Cerrar Sesión</span>
        </button>
      </div>
    </div>
  );
};

TechnicianMoreMenu.propTypes = {
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  unreadMessages: PropTypes.number
};

TechnicianMoreMenu.defaultProps = {
  unreadMessages: 0
};

export default TechnicianMoreMenu;
